import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Drawer,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  BugReport as DebugIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { openActionModal } from "../../../store/adminSlice";
import { TinyValueComponent } from "../../../internal/components/TinyValueComponent";
import { DbObjectIdComponent } from "../../../internal/components/DbObjectIdComponent";
import { EmailAdminActions } from "../../../internal/Email/EmailAdminActions";
import type { PipelineResultLight, PipelineResultFull } from "./types";
import { DiagnosticEventsDisplay } from "./DiagnosticEventsDisplay";
import { DiagnosticCountChips } from "./DiagnosticCountChips";
import { StepAccordion } from "./StepAccordion";
import { GptRequestsAccordion } from "./GptRequestsAccordion";
import { EmailAutomationPresentationComponent } from "@shared/msoffice";

interface ResultDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedResult: PipelineResultLight | null;
  detailedResult: PipelineResultFull | null;
  loadingDetails: boolean;
  onOpenEmail: (emailId: string, event: React.MouseEvent) => void;
  onSaveNotes?: (emailId: string, notes: string) => Promise<void>;
  activeTab?: number;
  onTabChange?: (tab: number) => void;
  /** Pipeline result ID - used for debug link */
  resultId?: string;
}

function GenerateActionsSection({
  generateActionsStep,
}: {
  generateActionsStep: NonNullable<PipelineResultFull["generateActionsStep"]>;
}) {
  const dispatch = useDispatch();
  const actions = generateActionsStep.resolvedActions || generateActionsStep.actions;
  const actionIds = generateActionsStep.actionIds;

  return (
    <StepAccordion
      title="Generate Actions"
      diagnostics={generateActionsStep.diagnostics}
    >
      <Typography variant="body2" fontWeight="medium" mb={1}>
        {generateActionsStep.actions.length} action(s) generated
        {actionIds && <> ({actionIds.length} saved)</>}
      </Typography>
      {actions.map((action: any, idx: number) => {
        const actionId = actionIds?.[idx];
        return (
          <Paper
            key={idx}
            sx={{
              p: 1.5,
              mb: 1,
              backgroundColor: "grey.50",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Chip label={action.actionType} size="small" color="primary" />
              {actionId && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => dispatch(openActionModal(actionId))}
                  sx={{ textTransform: "none" }}
                >
                  View Action
                </Button>
              )}
            </Box>
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              {JSON.stringify(action.data, null, 2)}
            </Box>
          </Paper>
        );
      })}
    </StepAccordion>
  );
}

export function ResultDetailDrawer({
  open,
  onClose,
  selectedResult,
  detailedResult,
  loadingDetails,
  onOpenEmail,
  onSaveNotes,
  activeTab,
  onTabChange,
  resultId,
}: ResultDetailDrawerProps) {
  const [localNotes, setLocalNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);
  const [tabValue, setTabValue] = useState(activeTab ?? 0);

  // Sync tab with parent state
  useEffect(() => {
    if (activeTab !== undefined) setTabValue(activeTab);
  }, [activeTab]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    onTabChange?.(newValue);
  };

  // Sync local notes with selected result
  useEffect(() => {
    if (selectedResult) {
      setLocalNotes(selectedResult.notes || "");
      setNotesChanged(false);
    }
  }, [selectedResult]);

  const handleNotesChange = (value: string) => {
    setLocalNotes(value);
    setNotesChanged(value !== (selectedResult?.notes || ""));
  };

  const handleSaveNotes = async () => {
    if (!selectedResult || !onSaveNotes) return;
    setSavingNotes(true);
    try {
      await onSaveNotes(selectedResult.emailId, localNotes);
      setNotesChanged(false);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", md: 600 } } }}
    >
      {selectedResult && (
        <Box sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6">Result Details</Typography>
              {resultId && (
                <DbObjectIdComponent
                  value={resultId}
                  table="emailAutomationPipelineResult"
                  prefix="rid:"
                />
              )}
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <IconButton
              size="small"
              onClick={(e) => onOpenEmail(selectedResult.emailId, e)}
              title="Open email"
            >
              <EmailIcon color="primary" />
            </IconButton>
            <Typography variant="subtitle2" color="text.secondary">
              Email ID:
            </Typography>
            <TinyValueComponent value={selectedResult.emailId} clipboard />
          </Box>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab label="Result Details" />
            <Tab label="Outlook Preview" />
          </Tabs>

          {/* Tab Panel: Result Details */}
          {tabValue === 0 && (
            <>
              {/* Notes Section */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: "grey.50" }}>
                <Typography variant="subtitle2" gutterBottom>
                  Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={localNotes}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  placeholder="Add notes about this email result..."
                  size="small"
                  sx={{ mb: 1 }}
                />
                {onSaveNotes && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={
                        savingNotes ? (
                          <CircularProgress size={14} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      onClick={handleSaveNotes}
                      disabled={!notesChanged || savingNotes}
                    >
                      {savingNotes ? "Saving..." : "Save Notes"}
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* Loading indicator while fetching full details */}
              {loadingDetails ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <CircularProgress size={32} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Loading full details...
                  </Typography>
                </Box>
              ) : detailedResult ? (
                <>
                  {/* Pipeline-Level Diagnostics */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <Typography>
                          Pipeline Logs (
                          {detailedResult.diagnosticEvents?.events?.length || 0}{" "}
                          events)
                        </Typography>
                        <Box sx={{ ml: "auto", mr: 1 }}>
                          <DiagnosticCountChips
                            events={detailedResult.diagnosticEvents}
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {detailedResult.diagnosticEvents && (
                        <DiagnosticEventsDisplay
                          events={detailedResult.diagnosticEvents}
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Categorization Step */}
                  <StepAccordion
                    title="Categorization"
                    diagnostics={detailedResult.categorizationStep?.diagnostics}
                  >
                    {detailedResult.categorizationStep ? (
                      <>
                        <Typography variant="body2" fontWeight="medium">
                          Categories:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 2,
                          }}
                        >
                          {detailedResult.categorizationStep.categories.map(
                            (cat) => (
                              <Chip key={cat} label={cat} size="small" />
                            ),
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                          Exchange Type:
                        </Typography>
                        <Typography variant="body2" mb={2}>
                          {detailedResult.categorizationStep.exchangeType}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          Summary:
                        </Typography>
                        <Typography variant="body2" mb={2}>
                          {detailedResult.categorizationStep.oneLineSummary}
                        </Typography>
                        {detailedResult.categorizationStep.context && (
                          <>
                            <Typography variant="body2" fontWeight="medium">
                              Context:
                            </Typography>
                            <Typography variant="body2" mb={2}>
                              {detailedResult.categorizationStep.context}
                            </Typography>
                          </>
                        )}
                      </>
                    ) : (
                      <Typography color="text.secondary">
                        Not executed
                      </Typography>
                    )}
                  </StepAccordion>

                  {/* Property Extraction Step */}
                  <StepAccordion
                    title="Property Extraction"
                    diagnostics={
                      detailedResult.propertyExtractionStep?.diagnostics
                    }
                  >
                    {detailedResult.propertyExtractionStep ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Property</TableCell>
                            <TableCell>Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailedResult.propertyExtractionStep.properties.map(
                            (prop, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{prop.name}</TableCell>
                                <TableCell>{prop.value}</TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography color="text.secondary">
                        Not executed
                      </Typography>
                    )}
                  </StepAccordion>

                  {/* API Fetch Step */}
                  <StepAccordion
                    title="API Fetch"
                    diagnostics={detailedResult.apiFetchStep?.diagnostics}
                  >
                    {detailedResult.apiFetchStep ? (
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: "grey.50",
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          whiteSpace: "pre-wrap",
                          maxHeight: 300,
                          overflow: "auto",
                        }}
                      >
                        {JSON.stringify(
                          detailedResult.apiFetchStep.data,
                          null,
                          2,
                        )}
                      </Paper>
                    ) : (
                      <Typography color="text.secondary">
                        Not executed
                      </Typography>
                    )}
                  </StepAccordion>

                  {/* Presentation Step */}
                  <StepAccordion
                    title="Presentation"
                    diagnostics={detailedResult.presentationStep?.diagnostics}
                  >
                    {detailedResult.presentationStep ? (
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: "grey.50",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {JSON.stringify(
                          detailedResult.presentationStep.sections,
                          null,
                          2,
                        )}
                      </Paper>
                    ) : (
                      <Typography color="text.secondary">
                        Not executed
                      </Typography>
                    )}
                  </StepAccordion>

                  {/* Generate Actions Step */}
                  {detailedResult.generateActionsStep && (
                    <GenerateActionsSection generateActionsStep={detailedResult.generateActionsStep} />
                  )}

                  {/* Eval Step */}
                  <StepAccordion
                    title="Evaluation"
                    diagnostics={detailedResult.evalStep?.diagnostics}
                  >
                    {detailedResult.evalStep ? (
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: "grey.50",
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {JSON.stringify(
                          detailedResult.evalStep.output,
                          null,
                          2,
                        )}
                      </Paper>
                    ) : (
                      <Typography color="text.secondary">
                        Not executed
                      </Typography>
                    )}
                  </StepAccordion>

                  {/* GPT Requests */}
                  <GptRequestsAccordion requests={detailedResult.gptRequests} />
                </>
              ) : (
                <Typography
                  color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}
                >
                  Failed to load details
                </Typography>
              )}
            </>
          )}

          {/* Tab Panel: Outlook Preview */}
          {tabValue === 1 && (
            <EmailAutomationPresentationComponent
              sections={detailedResult?.presentationStep?.sections || []}
              loading={loadingDetails}
              emptyMessage="No presentation data available. Run the presentation step to generate sections."

            />
          )}

          {/* Debug Button */}
          {resultId && (
            <Box
              sx={{
                position: "fixed",
                bottom: 16,
                right: 16,
              }}
            >
              <Button
                component={Link}
                to={`/admin/email-automation/result/${resultId}`}
                variant="outlined"
                size="small"
                startIcon={<DebugIcon />}
                sx={{ backgroundColor: "background.paper" }}
              >
                View Pipeline Result
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
}
